import * as React from "react";
import {
  IFree30Message,
  IFree30TimelineResponse,
  IFree30Note,
  IFree30Compliance,
  IFree30Specificity
} from "./free30-sequence.types";

const API_URL = process.env.REACT_APP_COACH_PERF_API_URL || "";
const API_KEY = process.env.REACT_APP_COACH_PERF_API_KEY || "";

export interface IUseFree30Timeline {
  messages: IFree30Message[] | null;
  sessionDate: string | null;
  notes: IFree30Note[];
  compliance: IFree30Compliance | null;
  specificity: IFree30Specificity | null;
  loading: boolean;
  error: string | null;
}

/** Lazy-load one guest's evidence timeline. `enabled` gates the fetch. */
export function useFree30Timeline(
  subdomain: string,
  pike13PersonId: number,
  enabled: boolean
): IUseFree30Timeline {
  const [messages, setMessages] = React.useState<IFree30Message[] | null>(null);
  const [sessionDate, setSessionDate] = React.useState<string | null>(null);
  const [notes, setNotes] = React.useState<IFree30Note[]>([]);
  const [compliance, setCompliance] = React.useState<IFree30Compliance | null>(
    null
  );
  const [
    specificity,
    setSpecificity
  ] = React.useState<IFree30Specificity | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!enabled || !API_URL || !API_KEY || !subdomain || !pike13PersonId)
      return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      subdomain,
      pike13_person_id: String(pike13PersonId),
      // Fetch message bodies so the exchange view can show the actual content.
      include_message_bodies: "true"
    });
    const url = `${API_URL}/evidence/free30?${params.toString()}`;

    fetch(url, { headers: { "X-API-Key": API_KEY } })
      .then(async res => {
        const body = await res.json().catch(() => null);
        if (!res.ok)
          throw new Error((body && body.message) || `HTTP ${res.status}`);
        return body as IFree30TimelineResponse;
      })
      .then(body => {
        if (cancelled) return;
        const ev = body.evidence;
        setMessages(ev?.messages || []);
        setSessionDate(ev?.subject?.firstScheduledSessionDate || null);
        setNotes(ev?.notes || []);
        setCompliance(ev?.compliance || null);
        setSpecificity(ev?.specificity_judgment || null);
      })
      .catch(e => {
        if (!cancelled) setError(e.message || "Failed to load timeline");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [subdomain, pike13PersonId, enabled]);

  return {
    messages,
    sessionDate,
    notes,
    compliance,
    specificity,
    loading,
    error
  };
}
